import * as Sentry from '@sentry/nextjs';

import api, { getCmsUrl } from '@/helpers/strapi/api';

import { LOCATION_ROUTES } from '../locationPages';

export const getAbsoluteImageUrl = relativeUrl => {
  if (!relativeUrl) return null;

  return new URL(relativeUrl, getCmsUrl()).toString();
};

export const fetchAllPages = async () => {
  const query = {
    pagination: {
      page: 1,
      pageSize: 100,
    },
  };

  const request = api.getPages(query);
  const requests = [request];

  const { data } = await request;
  const { page, pageCount } = data.meta.pagination;

  // `index` should start from the next/second page not to duplicate items
  for (let index = page + 1; index <= pageCount; index += 1) {
    const nextPageRequest = await api.getPages({
      ...query,
      pagination: { ...query.pagination, page: index },
    });

    requests.push(nextPageRequest);
  }

  const responses = await Promise.all(requests);

  return responses.reduce(
    (result, current) => [...result, ...current.data.data],
    []
  );
};

export const fetchAllBlogPosts = async () => {
  const query = {
    pagination: {
      page: 1,
      pageSize: 100,
    },
    populate: {
      blogCategory: {
        populate: {
          metadata: {
            populate: '*',
          },
        },
      },
      metadata: {
        populate: '*',
      },
    },
  };

  const request = await api.getBlogPosts(query);
  const requests = [request];

  const { data } = await request;
  const { page, pageCount } = data.meta.pagination;

  // `index` should start from the next/second page not to duplicate items
  for (let index = page + 1; index <= pageCount; index += 1) {
    const nextPageRequest = await api.getBlogPosts({
      ...query,
      pagination: { ...query.pagination, page: index },
    });

    requests.push(nextPageRequest);
  }
  const response = await Promise.all(requests);

  return response.reduce(
    (result, current) => [...result, ...current.data.data],
    []
  );
};

export const fetchAllReferenceProjects = async () => {
  const query = {
    pagination: {
      page: 1,
      pageSize: 100,
    },
    populate: {
      referenceCategory: {
        populate: {
          metadata: {
            populate: '*',
          },
        },
      },
      metadata: {
        populate: '*',
      },
    },
  };

  const request = await api.getReferenceProjects(query);
  const requests = [request];

  const { data } = await request;
  const { page, pageCount } = data.meta.pagination;

  // `index` should start from the next/second page not to duplicate items
  for (let index = page + 1; index <= pageCount; index += 1) {
    const nextPageRequest = await api.getReferenceProjects({
      ...query,
      pagination: { ...query.pagination, page: index },
    });

    requests.push(nextPageRequest);
  }
  const response = await Promise.all(requests);

  return response.reduce(
    (result, current) => [...result, ...current.data.data],
    []
  );
};

/**
 * Fetches all product types from the CMS API using pagination.
 * @async
 * @returns {Promise<Array>} Promise that resolves to an array of all product type data
 */
export const fetchProductTypes = async () => {
  const data = (await api.getProductTypes())?.data;

  if (!data?.data || !data?.meta) return [];

  const { pageCount } = data.meta.pagination;

  let products = [...(data.data || [])];

  if (pageCount > 1) {
    const promises = [];
    for (let currentPage = 2; currentPage <= pageCount; currentPage++) {
      promises.push(api.getProductTypes(currentPage));
    }
    const results = await Promise.allSettled(promises);

    results.forEach(nextPageData => {
      if (nextPageData.status === 'fulfilled') {
        products = products.concat(nextPageData.value.data.data || []);
      } else if (nextPageData.status === 'rejected') {
        Sentry.captureException(nextPageData.reason);
      }
    });
  }

  return products;
};

export const getLocations = async () => {
  const response = (await api.getLocations())?.data;

  if (!response?.data || !response?.meta) return [];

  const { pageCount } = response.meta.pagination;

  const locations = [...(response.data || [])];

  if (pageCount > 1) {
    const promises = [];
    for (let currentPage = 2; currentPage <= pageCount; currentPage++) {
      promises.push(api.getLocations(currentPage));
    }
    const results = await Promise.allSettled(promises);

    results.forEach(nextPageData => {
      if (nextPageData.status === 'fulfilled') {
        locations.push(...(nextPageData.value.data.data || []));
      } else if (nextPageData.status === 'rejected') {
        Sentry.captureException(nextPageData.reason);
      }
    });
  }

  const mappedLocations = locations.map(loc => ({
    id: loc.id,
    name: loc.attributes.city ?? null,
    state: loc.attributes.state ?? null,
    slug: loc.attributes.slug ?? null,
  }));

  return mappedLocations;
};

export const getLocationBySlug = async locationSlug => {
  const location = (await api.getLocationsBySlug(locationSlug))?.data
    ?.data?.[0];

  if (!location) {
    return null;
  }

  return {
    city: location?.attributes?.city ?? null,
    state: location?.attributes?.state ?? null,
    slug: location?.attributes?.slug ?? null,
    hero: location?.attributes?.hero?.data ?? null,
    text: location?.attributes?.text ?? null,
  };
};

export const getProductCategories = async () => {
  const categories = (await api.getProductCategories())?.data?.data;
  const mappedCategories = categories?.map(category => ({
    name: category?.attributes?.name ?? null,
    slug: category?.attributes?.slug ?? null,
    thumbnail: category?.attributes?.thumbnail ?? null,
  }));

  return mappedCategories;
};

const extractMinPrice = productTypes => {
  let minPrice = null;
  let priceBasis = null;
  for (const type of productTypes) {
    const price = type?.attributes?.blob?.rentalPrice;
    const basis = type?.attributes?.blob?.rentalPriceBasis;
    if (price != null && (minPrice === null || price < minPrice)) {
      minPrice = price;
      priceBasis = basis;
    }
  }
  return minPrice !== null ? { price: minPrice, priceBasis } : null;
};

export const getProductCategoryBySlug = async (
  categorySlug,
  { deep = false } = {}
) => {
  const categoryData = deep
    ? (await api.getProductCategoriesBySlugDeep(categorySlug))?.data?.data?.[0]
    : (await api.getProductCategoriesBySlug(categorySlug))?.data?.data?.[0];

  const productCategoryName = categoryData?.attributes?.name ?? null;
  const productFamilies =
    categoryData?.attributes?.product_families?.data ?? [];
  const mappedProductFamilies = productFamilies?.map(family => {
    const mapped = {
      name: family?.attributes?.name ?? null,
      slug: family?.attributes?.slug ?? null,
      thumbnail: family?.attributes?.thumbnail ?? null,
    };
    if (deep) {
      const allTypes = (family?.attributes?.product_groups?.data ?? []).flatMap(
        g => g?.attributes?.product_types?.data ?? []
      );
      mapped.minPrice = extractMinPrice(allTypes);
    }
    return mapped;
  });

  const result = {
    productCategoryName,
    productFamilies: mappedProductFamilies,
  };
  if (deep) {
    const allTypes = productFamilies
      .flatMap(f => f?.attributes?.product_groups?.data ?? [])
      .flatMap(g => g?.attributes?.product_types?.data ?? []);
    result.categoryMinPrice = extractMinPrice(allTypes);
  }
  return result;
};

export const getProductFamilyBySlug = async (
  productFamilySlug,
  productCategorySlug,
  { deep = false } = {}
) => {
  const productFamily = deep
    ? (
        await api.getProductFamiliesBySlugDeep(
          productFamilySlug,
          productCategorySlug
        )
      )?.data?.data?.[0]
    : (
        await api.getProductFamiliesBySlug(
          productFamilySlug,
          productCategorySlug
        )
      )?.data?.data?.[0];

  const productFamilyName = productFamily?.attributes?.name ?? null;
  const productCategoryName =
    productFamily?.attributes?.product_category?.data?.attributes?.name ?? null;

  const productGroups = productFamily?.attributes?.product_groups?.data ?? [];
  const mappedProductGroups = productGroups?.map(group => {
    const mapped = {
      name: group?.attributes?.title ?? null,
      slug: group?.attributes?.slug ?? null,
      thumbnail: group?.attributes?.thumbnail ?? null,
    };
    if (deep) {
      mapped.minPrice = extractMinPrice(
        group?.attributes?.product_types?.data ?? []
      );
    }
    return mapped;
  });

  const result = {
    productFamilyName,
    productCategoryName,
    productGroups: mappedProductGroups,
  };
  if (deep) {
    const allTypes = productGroups.flatMap(
      g => g?.attributes?.product_types?.data ?? []
    );
    result.familyMinPrice = extractMinPrice(allTypes);
  }
  return result;
};

export const getProductGroupBySlug = async (
  productGroupSlug,
  productFamilySlug,
  productCategorySlug
) => {
  const productGroup = (
    await api.getProductGroupsBySlug(
      productGroupSlug,
      productFamilySlug,
      productCategorySlug
    )
  )?.data?.data?.[0];

  const productGroupName = productGroup?.attributes?.title ?? null;
  const productFamilyName =
    productGroup?.attributes?.product_family?.data?.attributes?.name ?? null;
  const productCategoryName =
    productGroup?.attributes?.product_family?.data?.attributes?.product_category
      ?.data?.attributes?.name ?? null;
  const relatedProductTypes = productGroup?.attributes?.product_types?.data;

  const mappedProductTypes = relatedProductTypes?.map(type => ({
    name: type.attributes.name,
    slug: type.attributes.slug,
    imageUrl: type.attributes.blob.imageUrl,
    price: type.attributes.blob.rentalPrice,
    initialPrice: type.attributes.blob.initialPrice,
    salesforceId: type.attributes.blob.salesforceId,
    attributes: type.attributes.blob.attributes,
    rentalPriceBasis: type.attributes.blob.rentalPriceBasis,
  }));

  return {
    productGroupName,
    productFamilyName,
    productCategoryName,
    productTypes: mappedProductTypes ?? [],
  };
};

const getProductGroups = async () => {
  const data = (await api.getProductGroups())?.data;

  if (!data?.data || !data?.meta) return [];

  const { pageCount } = data.meta.pagination;

  const groups = [...(data.data || [])];

  if (pageCount > 1) {
    const promises = [];
    for (let currentPage = 2; currentPage <= pageCount; currentPage++) {
      promises.push(api.getProductGroups(currentPage));
    }
    const results = await Promise.allSettled(promises);

    results.forEach(nextPageData => {
      if (nextPageData.status === 'fulfilled') {
        groups.push(...(nextPageData.value.data.data || []));
      } else if (nextPageData.status === 'rejected') {
        Sentry.captureException(nextPageData.reason);
      }
    });
  }

  return groups.map(group => ({
    name: group?.attributes?.title ?? null,
    slug: group?.attributes?.slug ?? null,
    familySlug:
      group?.attributes?.product_family?.data?.attributes?.slug ?? null,
    categorySlug:
      group?.attributes?.product_family?.data?.attributes?.product_category
        ?.data?.attributes?.slug ?? null,
  }));
};

export const generateLocationBasedPathnames = async () => {
  const [locations, productGroups] = await Promise.all([
    getLocations(),
    getProductGroups(),
  ]);

  const urls = [];

  const filteredProductGroups = productGroups.filter(
    group => group.slug && group.familySlug && group.categorySlug
  );

  const {
    distinctCategories,
    distinctFamilies,
  } = extractDistinctCategoriesAndFamilies(filteredProductGroups);

  locations.forEach(location => {
    if (!location.slug) return;

    urls.push({
      pathname: `${LOCATION_ROUTES.BASE_PATH}/${location.slug}`,
    });

    distinctCategories.forEach(category => {
      urls.push({
        pathname: `${LOCATION_ROUTES.BASE_PATH}/${location.slug}/${category.categorySlug}`,
      });
    });

    distinctFamilies.forEach(family => {
      urls.push({
        pathname: `${LOCATION_ROUTES.BASE_PATH}/${location.slug}/${family.categorySlug}/${family.familySlug}`,
      });
    });

    filteredProductGroups.forEach(productGroup => {
      urls.push({
        pathname: `${LOCATION_ROUTES.BASE_PATH}/${location.slug}/${productGroup.categorySlug}/${productGroup.familySlug}/${productGroup.slug}`,
      });
    });
  });

  return urls;
};

const extractDistinctCategoriesAndFamilies = productGroups => {
  const categoryMap = new Map();
  const familyMap = new Map();

  productGroups.forEach(group => {
    categoryMap.set(group.categorySlug, {
      categorySlug: group.categorySlug,
    });
    familyMap.set(group.familySlug, {
      familySlug: group.familySlug,
      categorySlug: group.categorySlug,
    });
  });

  const distinctFamilies = Array.from(familyMap.values());
  const distinctCategories = Array.from(categoryMap.values());

  return { distinctCategories, distinctFamilies };
};

export const getPricesBySlugsIndependent = async slugs => {
  const pricesMap = {};
  if (!slugs || slugs.length === 0) return pricesMap;

  try {
    const [groupsRes, familiesRes] = await Promise.all([
      api.getProductGroupsBySlugsDeep(slugs),
      api.getProductFamiliesBySlugsDeep(slugs),
    ]);

    const groups = groupsRes?.data?.data ?? [];
    for (const group of groups) {
      if (!group?.attributes?.slug) continue;
      const allTypes = group.attributes.product_types?.data ?? [];
      const minPrice = extractMinPrice(allTypes);
      if (minPrice) {
        pricesMap[group.attributes.slug] = minPrice;
      }
    }

    const families = familiesRes?.data?.data ?? [];
    for (const family of families) {
      if (!family?.attributes?.slug) continue;
      if (pricesMap[family.attributes.slug]) continue;

      const allTypes = (family.attributes.product_groups?.data ?? []).flatMap(
        g => g?.attributes?.product_types?.data ?? []
      );
      const minPrice = extractMinPrice(allTypes);
      if (minPrice) {
        pricesMap[family.attributes.slug] = minPrice;
      }
    }
  } catch (error) {
    Sentry.captureException(error);
  }
  return pricesMap;
};
